import { useEffect, useRef } from 'react';
import { trackProductEvent } from '../utils/productAnalytics';

type UseSectionExposureAnalyticsArgs = {
  activeTab: string;
  periodKey: string;
  role: string;
};

const MIN_DURATION_MS = 1000;

function getSectionName(element: HTMLElement): string {
  return element.dataset.section || element.dataset.testid || 'unknown-section';
}

export function useSectionExposureAnalytics({
  activeTab,
  periodKey,
  role,
}: UseSectionExposureAnalyticsArgs): void {
  const visibleSectionsRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const visibleSections = visibleSectionsRef.current;
    const viewedSections = new Set<string>();
    visibleSections.clear();

    const flushSectionDuration = (section: string, reason: string) => {
      const startedAt = visibleSections.get(section);
      if (!startedAt) return;

      visibleSections.delete(section);
      const durationMs = Date.now() - startedAt;
      if (durationMs < MIN_DURATION_MS) return;

      trackProductEvent('section_duration', {
        section,
        tab: activeTab,
        period_key: periodKey,
        role,
        duration_ms: durationMs,
        reason,
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const element = entry.target as HTMLElement;
          const section = getSectionName(element);
          const isMeaningfullyVisible = entry.isIntersecting && entry.intersectionRatio >= 0.4;

          if (isMeaningfullyVisible) {
            if (!visibleSections.has(section)) {
              visibleSections.set(section, Date.now());
            }

            const viewKey = `${activeTab}:${periodKey}:${section}`;
            if (!viewedSections.has(viewKey)) {
              viewedSections.add(viewKey);
              trackProductEvent('section_viewed', {
                section,
                tab: activeTab,
                period_key: periodKey,
                role,
              });
            }
          } else {
            flushSectionDuration(section, 'left_viewport');
          }
        }
      },
      {
        threshold: [0, 0.4, 0.75],
        rootMargin: '0px 0px -10% 0px',
      }
    );

    const frame = window.requestAnimationFrame(() => {
      document.querySelectorAll<HTMLElement>('[data-section]').forEach((section) => {
        observer.observe(section);
      });
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        for (const section of Array.from(visibleSections.keys())) {
          flushSectionDuration(section, 'page_hidden');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      observer.disconnect();

      for (const section of Array.from(visibleSections.keys())) {
        flushSectionDuration(section, 'route_changed');
      }
    };
  }, [activeTab, periodKey, role]);
}
